/**
 * Universal CRUD list path SSOT — BusinessDomain → GET list mount path
 *
 * Backend convention: /api/domain/{subdomain}/{typeSegment}/{typeSegment}
 * (type segment duplicated — e.g. knowledge/plan/plan, entity/internal/internal)
 *
 * Consumers: backendApiClient.getApiPath, check:domain-route-parity
 */

/** Navigable domains that use Flow/legacy APIs — not Universal CRUD list mounts */
export const NON_UNIVERSAL_CRUD_LIST_DOMAINS = new Set<string>([
  'flow_definition',
  'stream_definition',
  'action_binding',
  'flow_execution',
  'stream_execution',
  'atomic_flow',
  'stream',
  'flow_assignee',
  'flow_template',
]);

/**
 * Explicit BusinessDomain → Universal CRUD list path (aligned with api domain route mounts)
 */
const UNIVERSAL_CRUD_LIST_PATH_BY_DOMAIN: Readonly<Record<string, string>> = {
  // Entity
  internal_entity: '/api/domain/entity/internal/internal',
  external_entity: '/api/domain/entity/external/external',
  /** Grammar proxy — supplier list reuses external entity CRUD mount */
  supplier: '/api/domain/entity/external/external',
  user_entity: '/api/domain/entity/user/user',

  // Item
  sellable_item: '/api/domain/item/sellable/sellable',
  service_item: '/api/domain/item/service/service',
  asset_item: '/api/domain/item/asset/asset',
  consumable_item: '/api/domain/item/consumable/consumable',
  raw_material_item: '/api/domain/item/raw-material/raw-material',

  // Finance
  finance_state: '/api/domain/finance/state/state',
  finance_flow: '/api/domain/finance/flow/flow',
  finance_budget: '/api/domain/finance/budget/budget',

  // Finance advanced
  legal_entity: '/api/domain/finance-advanced/legal-entity/legal-entity',
  currency_rate: '/api/domain/finance-advanced/currency-rate/currency-rate',
  intercompany: '/api/domain/finance-advanced/intercompany/intercompany',

  // Space
  physical_space: '/api/domain/space/physical/physical',
  virtual_space: '/api/domain/space/virtual/virtual',

  // Knowledge
  knowledge_report: '/api/domain/knowledge/report/report',
  knowledge_policy: '/api/domain/knowledge/policy/policy',
  knowledge_plan: '/api/domain/knowledge/plan/plan',

  // Appeal (API slugs use underscores — not URL hyphens)
  semantic_formation: '/api/domain/appeal/semantic_formation/semantic_formation',
  ephemeral_formation: '/api/domain/appeal/ephemeral_formation/ephemeral_formation',
  ephemeral_performance: '/api/domain/appeal/ephemeral_performance/ephemeral_performance',
  recurrent_performance: '/api/domain/appeal/recurrent_performance/recurrent_performance',

  // Calendar
  calendar_anchor: '/api/domain/calendar/event/event',
  calendar_trigger: '/api/domain/calendar/trigger/trigger',
  calendar_cycle: '/api/domain/calendar/cycle/cycle',

  // Log
  flow_event: '/api/domain/log/flow/flow',
  stream_event: '/api/domain/log/stream/stream',

  // Manufacturing
  manufacturing_bom: '/api/domain/manufacturing/bom/bom',
  production_order: '/api/domain/manufacturing/production-order/production-order',
  quality_inspection: '/api/domain/manufacturing/quality-inspection/quality-inspection',
};

/** All domains with an explicit Universal CRUD list mapping */
export function getUniversalCrudListMappedDomains(): readonly string[] {
  return Object.keys(UNIVERSAL_CRUD_LIST_PATH_BY_DOMAIN);
}

/**
 * Resolve Universal CRUD list GET path for a business domain.
 * Returns null when the domain uses Flow/legacy APIs or has no CRUD list mount.
 */
export function resolveDomainApiListPath(businessDomain: string): string | null {
  return UNIVERSAL_CRUD_LIST_PATH_BY_DOMAIN[businessDomain] ?? null;
}
